import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Users, Heart, Ticket, BookOpen } from "lucide-react";
import PageHero from "../components/PageHero";
import { submitMemberApplication } from "../lib/api";

const BENEFITS = [
  { icon: Ticket, title: "Accesso agevolato a concerti ed eventi", text: "Tariffe riservate ai soci per concerti, masterclass ed eventi della scuola." },
  { icon: BookOpen, title: "Materiali didattici e risorse", text: "Accesso al patrimonio di spartiti, registrazioni e dispense della scuola." },
  { icon: Users, title: "Partecipazione alla vita associativa", text: "Assemblee, laboratori collettivi, progetti artistici e musica d'insieme." },
  { icon: Heart, title: "Sostegno all'arte e alla cultura", text: "Contribuisci alla promozione della musica sul territorio." },
];

export default function AreaSoci() {
  const [form, setForm] = useState({ nome: "", cognome: "", email: "", telefono: "", motivazione: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await submitMemberApplication(form);
      setDone(true);
      toast.success("Richiesta inviata!");
    } catch {
      toast.error("Errore durante l'invio. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-area-soci">
      <PageHero
        eyebrow="Amici per la Musica APS"
        title="Diventa"
        italic="socio."
        subtitle="Unisciti alla nostra Associazione di Promozione Sociale. Un gesto che sostiene la musica, la formazione artistica e la vita culturale della comunità."
      />

      <section className="py-24 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Benefits */}
        <div className="lg:col-span-7">
          <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-4">I vantaggi</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#1C1C1C] leading-tight mb-10">
            Cosa significa<br />essere socio.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#A3133A]/15 border border-[#A3133A]/15">
            {BENEFITS.map((b, i) => (
              <div key={b.title} className="bg-[#FBFBF9] p-7" data-testid={`socio-benefit-${i}`}>
                <b.icon size={22} className="text-[#A3133A] mb-4" />
                <div className="font-serif-display text-xl text-[#1C1C1C] mb-2">{b.title}</div>
                <p className="text-sm text-[#5C5C5C]">{b.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 border-l-2 border-[#A3133A] pl-6 py-2">
            <p className="text-sm text-[#5C5C5C] italic">
              Nota: quest'area ospiterà in futuro il portale riservato ai soci
              (accesso materiali, calendario personale, gestione quote). Per ora
              compila la richiesta di adesione qui sotto.
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-5">
          <div className="bg-[#A3133A] text-[#FBFBF9] p-8 sm:p-10 sticky top-28 relative grain overflow-hidden">
            <div className="relative z-10">
              <div className="text-[11px] tracking-eyebrow opacity-80 mb-4">Modulo di adesione</div>
              <h3 className="font-serif-display text-3xl mb-6">Richiesta di iscrizione a socio</h3>

              {done ? (
                <div data-testid="socio-success" className="text-center py-6">
                  <CheckCircle2 size={48} className="mx-auto mb-4 opacity-90" />
                  <p className="opacity-90">Grazie! Ti contatteremo per completare l'adesione all'associazione.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="socio-form">
                  <SociInput label="Nome" value={form.nome} onChange={(v) => update("nome", v)} testid="socio-nome" required />
                  <SociInput label="Cognome" value={form.cognome} onChange={(v) => update("cognome", v)} testid="socio-cognome" required />
                  <SociInput label="Email" type="email" value={form.email} onChange={(v) => update("email", v)} testid="socio-email" required />
                  <SociInput label="Telefono" type="tel" value={form.telefono} onChange={(v) => update("telefono", v)} testid="socio-telefono" required />
                  <div>
                    <label className="block text-[11px] tracking-eyebrow opacity-80 mb-2">Motivazione (opzionale)</label>
                    <textarea
                      value={form.motivazione}
                      onChange={(e) => update("motivazione", e.target.value)}
                      data-testid="socio-motivazione"
                      rows={3}
                      className="w-full bg-transparent border border-white/30 rounded-sm px-3 py-2 text-sm placeholder:text-white/50 focus:outline-none focus:border-white"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="socio-submit"
                    className="w-full bg-white text-[#A3133A] py-3 text-[12px] tracking-eyebrow rounded-sm hover:bg-white/90 disabled:opacity-60"
                  >
                    {loading ? "Invio..." : "Invia richiesta"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const SociInput = ({ label, value, onChange, type = "text", testid, required }) => (
  <div>
    <label className="block text-[11px] tracking-eyebrow opacity-80 mb-2">{label}</label>
    <input
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full bg-transparent border border-white/30 rounded-sm px-3 py-2 text-sm placeholder:text-white/50 focus:outline-none focus:border-white"
    />
  </div>
);
